// License Admin API Service - Calls Firebase Functions for license management
// This is separate from the main API because it calls Firebase directly (your cloud)
//
// NOTE: This requires Firebase to be initialized. If using the Postgres backend
// without Firebase, the License Admin will show a configuration message.

import { getToken } from './api';

// Firebase Functions URL (adjust based on your project)
const FUNCTIONS_URL = import.meta.env.VITE_FIREBASE_FUNCTIONS_URL || 'https://us-central1-vincikit-bd9e1.cloudfunctions.net';

// Check if we're in Firebase mode or Backend mode
const isFirebaseMode = () => {
  try {
    // Check if Firebase app is initialized
    const { getApp } = require('firebase/app');
    getApp();
    return true;
  } catch {
    return false;
  }
};

// Get auth token - use Firebase if available, otherwise use backend JWT
async function getAuthToken() {
  if (isFirebaseMode()) {
    const { getAuth } = require('firebase/auth');
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated with Firebase');
    }
    return user.getIdToken();
  } else {
    // Use backend JWT token
    const token = getToken();
    if (!token) {
      throw new Error('Not authenticated');
    }
    return token;
  }
}

// Authenticated fetch helper for Firebase Functions
async function firebaseFetch(functionName, body = {}) {
  // Check if Firebase Functions URL is configured and reachable
  if (!FUNCTIONS_URL || FUNCTIONS_URL.includes('localhost:5001')) {
    // Local emulator mode - check if emulator is running
    try {
      const token = await getAuthToken();
      const response = await fetch(`${FUNCTIONS_URL}/${functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      return response.json();
    } catch (error) {
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('Firebase Functions emulator not running. Start with: cd functions && firebase emulators:start --only functions');
      }
      throw error;
    }
  }

  const token = await getAuthToken();

  const response = await fetch(`${FUNCTIONS_URL}/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  return response.json();
}

// =====================================================
// LICENSE MANAGEMENT
// =====================================================

export async function getLicenseAdminStats() {
  return firebaseFetch('licenseAdmin', { action: 'getDashboardStats' });
}

export async function listLicenses(filters = {}) {
  return firebaseFetch('licenseAdmin', {
    action: 'listLicenses',
    ...filters
  });
}

export async function getLicense(licenseId) {
  return firebaseFetch('licenseAdmin', {
    action: 'getLicense',
    licenseId
  });
}

export async function createLicense(licenseData) {
  return firebaseFetch('licenseAdmin', {
    action: 'createLicense',
    ...licenseData
  });
}

export async function renewLicense(licenseId, options = {}) {
  return firebaseFetch('licenseAdmin', {
    action: 'renewLicense',
    licenseId,
    ...options
  });
}

export async function updateLicenseStatus(licenseId, status, reason) {
  return firebaseFetch('licenseAdmin', {
    action: 'updateLicenseStatus',
    licenseId,
    status,
    reason
  });
}

export async function upgradeLicense(licenseId, newTier, prorateCredit = true) {
  return firebaseFetch('licenseAdmin', {
    action: 'upgradeLicense',
    licenseId,
    newTier,
    prorateCredit
  });
}

// =====================================================
// TRANSACTIONS
// =====================================================

export async function listTransactions(filters = {}) {
  return firebaseFetch('licenseAdmin', {
    action: 'listTransactions',
    ...filters
  });
}

export async function addTransaction(transactionData) {
  return firebaseFetch('licenseAdmin', {
    action: 'addTransaction',
    ...transactionData
  });
}

// =====================================================
// TRIAL PACKAGES
// =====================================================

export async function listTrialPackages(active) {
  return firebaseFetch('licenseAdmin', {
    action: 'listTrialPackages',
    active
  });
}

export async function createTrialPackage(packageData) {
  return firebaseFetch('licenseAdmin', {
    action: 'createTrialPackage',
    ...packageData
  });
}

export async function updateTrialPackage(packageId, updates) {
  return firebaseFetch('licenseAdmin', {
    action: 'updateTrialPackage',
    packageId,
    updates
  });
}

export async function redeemTrial(packageCode, organization, email) {
  return firebaseFetch('licenseAdmin', {
    action: 'redeemTrial',
    packageCode,
    organization,
    email
  });
}

export async function deleteTrialPackage(packageId) {
  return firebaseFetch('licenseAdmin', {
    action: 'deleteTrialPackage',
    packageId
  });
}

// =====================================================
// ACTIVITY LOGS
// =====================================================

export async function getLicenseLogs(filters = {}) {
  return firebaseFetch('licenseAdmin', {
    action: 'getLogs',
    ...filters
  });
}

// =====================================================
// LICENSE DELIVERY
// =====================================================

export async function generateDeliveryPackage(licenseId, options = {}) {
  return firebaseFetch('licenseAdmin', {
    action: 'generateDeliveryPackage',
    licenseId,
    options
  });
}

export async function getDeliveryPreview(licenseId, fileType = 'readme') {
  return firebaseFetch('licenseAdmin', {
    action: 'getDeliveryPreview',
    licenseId,
    fileType
  });
}

export async function sendDeliveryEmail(licenseId, recipientEmail, customMessage = '') {
  return firebaseFetch('licenseAdmin', {
    action: 'sendDeliveryEmail',
    licenseId,
    recipientEmail,
    customMessage
  });
}

export async function listDeliveries(licenseId = null, limit = 20) {
  return firebaseFetch('licenseAdmin', {
    action: 'listDeliveries',
    licenseId,
    limit
  });
}

// Helper to download package as ZIP file (client-side)
export async function downloadDeliveryPackage(licenseId, options = {}) {
  const result = await generateDeliveryPackage(licenseId, options);

  if (!result.success) {
    throw new Error(result.error || 'Failed to generate package');
  }

  // Create ZIP file using JSZip (must be imported in component)
  return result.package;
}

// =====================================================
// TRIAL PACKAGE DELIVERY
// =====================================================

export async function generateTrialPackage(packageId, organization, email, options = {}) {
  return firebaseFetch('licenseAdmin', {
    action: 'generateTrialPackage',
    packageId,
    organization,
    email,
    options
  });
}

export async function getTrialPackagePreview(packageId, fileType = 'readme') {
  return firebaseFetch('licenseAdmin', {
    action: 'getTrialPackagePreview',
    packageId,
    fileType
  });
}

export async function sendTrialPackageEmail(packageId, recipientEmail, organization, customMessage = '') {
  return firebaseFetch('licenseAdmin', {
    action: 'sendTrialPackageEmail',
    packageId,
    recipientEmail,
    organization,
    customMessage
  });
}

// =====================================================
// LICENSE TIERS (constant, matches backend)
// =====================================================

export const LICENSE_TIERS = {
  starter: {
    name: 'Starter',
    priceCents: 29900,
    price: 299,
    users: 50,
    requests: 500,
    features: ['basic_analytics', 'email_support'],
    color: 'blue'
  },
  professional: {
    name: 'Professional',
    priceCents: 79900,
    price: 799,
    users: 200,
    requests: 2000,
    features: ['advanced_analytics', 'priority_support', 'custom_integrations'],
    color: 'purple'
  },
  enterprise: {
    name: 'Enterprise',
    priceCents: 249900,
    price: 2499,
    users: -1,
    requests: -1,
    features: ['all', 'dedicated_support', 'custom_development', 'on_premise'],
    color: 'amber'
  }
};

export const LICENSE_STATUS = {
  active: { label: 'Active', color: 'green' },
  expired: { label: 'Expired', color: 'red' },
  suspended: { label: 'Suspended', color: 'yellow' },
  cancelled: { label: 'Cancelled', color: 'gray' },
  trial: { label: 'Trial', color: 'blue' },
  pending: { label: 'Pending', color: 'orange' }
};

export const TRANSACTION_TYPES = {
  purchase: { label: 'Purchase', color: 'green' },
  renewal: { label: 'Renewal', color: 'blue' },
  upgrade: { label: 'Upgrade', color: 'purple' },
  downgrade: { label: 'Downgrade', color: 'orange' },
  refund: { label: 'Refund', color: 'red' },
  trial_conversion: { label: 'Trial Conversion', color: 'cyan' }
};
